// photo-utils.ts
import { LogEntryPhoto } from '../../models/logs/photos/LogEntryPhoto.interface';
import { PhotoMetadata } from '../../models/logs/photos/PhotoMetadata';

/**
 * Converts LogEntryPhoto objects to File objects
 * @param logEntryPhotos Array of LogEntryPhoto objects
 * @returns Promise that resolves to an array of File objects
 */
export async function convertPhotosToFiles(logEntryPhotos: LogEntryPhoto[]): Promise<File[]> {
  // Convert base64 URLs back to Files
  const photoFiles: File[] = await Promise.all(
    logEntryPhotos.map(async (photo, index) => {
      const response = await fetch(photo.url);
      const blob = await response.blob();
      return new File([blob], photo.name || `photo-${index}.jpg`, { type: blob.type });
    })
  );
  return photoFiles;
}


/**
 * Converts Azure blob SAS URLs to File objects
 * @param azurePhotos Array of objects containing photo metadata with SAS URLs
 * @returns Promise that resolves to an array of File objects
 */
export async function convertAzurePhotosToFiles(azurePhotos: PhotoMetadata[]): Promise<File[]> {
  if (!azurePhotos || azurePhotos.length === 0) {
    return [];
  }

  // Convert SAS URLs to Files
  const photoFiles: File[] = await Promise.all(
    azurePhotos.map(async (photo, index) => {
      try {
        // Fetch the blob data using the SAS URL
        const response = await fetch(photo.url);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch blob: ${response.status} ${response.statusText}`);
        }
        
        // Get the blob from the response
        const blob = await response.blob();
        
        // Create a File object from the blob
        const fileName = photo.blobName || `photo-${photo.id || index}.jpg`;
        const fileType = blob.type || 'image/jpeg';
        
        return new File([blob], fileName, { type: fileType });
      } catch (error) {
        console.error(`Error fetching photo ${photo.id || index}:`, error);
        throw error;
      }
    })
  );
  
  return photoFiles;
}


/**
 * Groups photos so every photo of the same kind sits together, for viewers that
 * show a log's whole photo set at once (a log can hold several photos per kind,
 * and the API returns them interleaved).
 *
 * Kinds are ordered by where each one first appears in the incoming array, and
 * photos keep their relative order inside a kind — so the backend's ordering is
 * preserved, just gathered. Photos with no kind group together like any other.
 *
 * @param photos The photos as fetched, in API order.
 * @returns A new array with same-kind photos adjacent; the input is untouched.
 */
export function groupPhotosByKind<T extends { kind?: string }>(photos: T[]): T[] {
  if (!photos || photos.length === 0) {
    return [];
  }

  const groups = new Map<string, T[]>();
  for (const photo of photos) {
    const kind = photo.kind ?? '';
    const group = groups.get(kind);
    if (group) {
      group.push(photo);
    } else {
      groups.set(kind, [photo]);
    }
  }

  return Array.from(groups.values()).flat();
}


/** One kind's photos, with the heading to show above them. */
export interface PhotoKindGroup<T> {
  /** The raw kind as sent by the API; empty string for photos without one. */
  kind: string;
  /** Heading text — see `photoKindLabel`. */
  label: string;
  photos: T[];
}

/**
 * Headings for the kinds the app knows about. Anything else falls back to the
 * kind itself, so a kind added on the backend shows up under its own name rather
 * than disappearing into "Other".
 */
const KIND_LABELS: Record<string, string> = {
  slip: 'Packing slips',
  damage: 'Damage photos',
  shipment: 'Shipment photos',
};

/**
 * The heading for a kind: a friendly plural for the known ones, the kind's own
 * text otherwise, and a catch-all for photos the API sent no kind for.
 */
export function photoKindLabel(kind: string | undefined | null): string {
  if (!kind) { return 'Other photos'; }
  return KIND_LABELS[kind.trim().toLowerCase()] ?? kind;
}

/**
 * Splits photos into one labelled group per kind, for views that show each kind
 * under its own heading instead of labelling every thumbnail.
 *
 * Same ordering as `groupPhotosByKind` — groups in order of first appearance,
 * photos in API order within a group — so the two stay consistent when a caller
 * uses the flat list for something else (downloads, email).
 *
 * @param photos The photos as fetched, in API order.
 * @returns One group per distinct kind; empty for no photos.
 */
export function photoKindGroups<T extends { kind?: string }>(photos: T[]): PhotoKindGroup<T>[] {
  if (!photos || photos.length === 0) {
    return [];
  }

  const groups = new Map<string, PhotoKindGroup<T>>();
  for (const photo of photos) {
    const kind = photo.kind ?? '';
    const group = groups.get(kind);
    if (group) {
      group.photos.push(photo);
    } else {
      groups.set(kind, { kind, label: photoKindLabel(kind), photos: [photo] });
    }
  }

  return Array.from(groups.values());
}


  /**
   * Converts PhotoMetadata to LogEntryPhoto format for components that need LogEntryPhoto
   */
  export  function convertAzurePhotosToLogEntryPhotos(azurePhotos: PhotoMetadata[]):LogEntryPhoto[] {
    if (!azurePhotos || azurePhotos.length === 0) {
      return [];
    }

    return azurePhotos.map((photo) => {
      return {
        id: photo.id.toString(),
        name: photo.blobName,
        url: photo.url
      } as LogEntryPhoto;
    });
  }
